import React, { useEffect, useMemo, useRef, useState } from "react";

import { Attribute, MdmDeviceDto, MdmObjectDto, TablesDetailsInput, useUpdateTableDetailsForUserMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Path } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { exportExcel, formatDateString } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { CustomRowData, MdmTableName, TableColumn } from "../utils/types";
import { errorToaster, findObjectValue, renderCustomFieldValue, successToaster } from "../utils/utils";
import TableColumnSelection from "./TableColumnSelection";
import TableToolbar from "./TableToolbar";

type ItemsTableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  searchFields?: Path<T>[];
  toolbarBtnClassName?: string;
  toolbarBtnLabel?: string;
  onToolbarBtnClick?: () => void;
  loading?: boolean;
  showToolbar?: boolean;
  showToolbarBtn?: boolean;
  tableName: MdmTableName;
  setFilteredData?: (data: T[]) => void;
};
const ItemsTable = <T,>({
  columns,
  data,
  onToolbarBtnClick = () => {},
  searchFields = [],
  toolbarBtnClassName = "",
  toolbarBtnLabel,
  loading,
  showToolbar = true,
  showToolbarBtn = true,
  tableName,
  setFilteredData,
}: ItemsTableProps<T>) => {
  const { userSettings, project } = useProjectContext();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [filterItems, setFilterItems] = useState<T[]>(data);
  const [excelData, setExcelData] = useState<T[]>([]);
  const tableDetails = useUserTableDetails(userSettings, tableName);
  const [customColumns, setCustomColumns] = useState<string[]>(() => {
    const columnList = tableDetails?.columnOrders?.map(r => r!.name) ?? [];
    if (userSettings && columnList.length > 0) {
      return columnList;
    }
    return columns.map(col => col.columnKey);
  });
  const dt = useRef<DataTable>(null);

  const [updateTableDetails, { loading: updateLoading }] = useUpdateTableDetailsForUserMutation({
    onError: errorToaster,
  });

  const customCols: Attribute[] = useMemo(
    () =>
      tableName === "DevicesByObject"
        ? [
            ...((project?.mdmProjectById.configuration?.deviceConfiguration
              .customFields as Attribute[]) ?? []),
          ]
        : [
            ...((project?.mdmProjectById.configuration?.objectConfiguration
              .customFields as Attribute[]) ?? []),
          ],
    [
      project?.mdmProjectById.configuration?.deviceConfiguration.customFields,
      project?.mdmProjectById.configuration?.objectConfiguration.customFields,
      tableName,
    ]
  );

  const mapCols = useMemo(
    () =>
      [
        ...columns,
        ...(customCols?.map(col => ({
          field: `${col?.id}.value`,
          header: T((col?.title ?? "") as StringKey),
          body: (rowData: CustomRowData) =>
            renderCustomFieldValue(rowData, col?.id as keyof CustomRowData, customCols),
          sortable: true,
          filter: true,
          columnKey: col?.id,
        })) ?? []),
      ] as TableColumn<T>[],
    [columns, customCols]
  );

  const formatObjectFields = useMemo(
    () =>
      filterItems.reduce((acc, d) => {
        const customFields =
          tableName === "DevicesByObject"
            ? (d as { mdmDevice: MdmDeviceDto }).mdmDevice?.customFields
            : (d as { mdmObject: MdmObjectDto }).mdmObject?.customFields;
        const objFields = customFields?.reduce((accObj, field) => {
          const newObj = {
            value: field?.value?.value,
            id: field?.key,
            title: customCols.find(col => col?.id === field?.key)?.title,
          };
          return {
            ...accObj,
            [field?.key!]: newObj,
          };
        }, {});

        acc.push({
          ...d,
          ...objFields,
        });

        return acc;
      }, [] as any[]),
    [customCols, filterItems, tableName]
  );

  const filteredColumns = useMemo(
    () => mapCols.filter(col => customColumns.includes(col.columnKey)),
    [customColumns, mapCols]
  );

  useEffect(() => {
    if(formatObjectFields) {
      setExcelData(formatObjectFields);
      dt.current?.reset();
    }
  }, [customColumns, formatObjectFields]);

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const handleExportExcel = () => {
    const tableData = excelData.map(row => {
      const newData: any = {};

      filteredColumns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field
          ? formatDateString(findObjectValue(row, col.field)?.toString())
          : col.body;
      });
      return newData;
    });
    exportExcel(tableData, tableName);
  }

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    setPage(e.first);
    if (pageLoaded && tableDetails?.rows !== e.rows) {
      updateTableDetails({
        variables: { projectId: project?.mdmProjectById.id!, tableDetails: tableDetailsInput },
      });
    }
    setPageLoaded(true);
  };

  const handleColumnSave = () => {
    updateTableDetails({
      variables: {
        projectId: project?.mdmProjectById.id!,
        tableDetails: {
          table: tableName,
          rows: tableDetails?.rows || 10,
          columnOrders: customColumns.map((col, i) => ({
            name: col,
            order: i,
          })),
        },
      },
    onCompleted: () => successToaster(T("alert.columns-saved-successfully")),
    });
  };

  return (
    <>
      {showToolbar && (
        <TableToolbar
          searchData={data}
          onSearch={setFilterItems}
          onClickBtn={onToolbarBtnClick}
          searchFields={searchFields}
          classNameBtn={toolbarBtnClassName}
          showBtn={showToolbarBtn}
          labelBtn={toolbarBtnLabel}
          additionalControls={
            <>
              <Button
                type="button"
                icon="pi pi-file"
                className="p-button-outlined p-button-warning"
                title={T("action.export-to-csv")}
                onClick={exportCSV}
              />
              <Button
                type="button"
                icon="pi pi-file-excel"
                className="p-button-outlined p-button-warning"
                title={T("action.export-to-excel")}
                onClick={handleExportExcel}
              />
              <TableColumnSelection
                customColumns={customColumns}
                setCustomColumns={setCustomColumns}
                handleColumnSave={handleColumnSave}
                mapCol={mapCols}
                loading={updateLoading}
              />
            </>
          }
        />
      )}
      <DataTable
        ref={dt}
        value={formatObjectFields}
        onValueChange={tableData => {
          setExcelData(tableData);
          setFilteredData?.(tableData);
        }}
        stripedRows
        paginator
        rows={tableDetails?.rows || 10}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate={T("table.page-report-template", {
          first: "{first}",
          last: "{last}",
          totalRecords: "{totalRecords}",
        })}
        loading={loading}
        size="small"
        className="overflow-x-auto"
        first={page}
        onPage={onPage}
      >
        {filteredColumns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </>
  );
};

export default ItemsTable;
